/**
 * hooks de cotacoes + cesta por item.
 *
 * invalidation cruzada importante: syncPncp e formarCesta podem mudar
 * status de cotacoes (sync cria novas, formar marca como EXCLUIDA_OUTLIER),
 * entao ambos invalidam a query de cotacoes + a de cesta.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CestaDetail,
  CestaFormar,
  Cotacao,
  CotacaoCreate,
  CotacaoListResponse,
} from '@licita-mind/shared-types';
import { apiFetch } from '../lib/api';

export const cotacoesKey = (itemId: string) => ['cotacoes', itemId] as const;
export const cestaKey = (itemId: string) => ['cesta', itemId] as const;

// ============ cotacoes ============

export const useListCotacoes = (itemId: string | undefined) =>
  useQuery({
    queryKey: cotacoesKey(itemId ?? '__none__'),
    queryFn: () => apiFetch<CotacaoListResponse>(`/v1/itens/${itemId}/cotacoes`),
    enabled: !!itemId,
    staleTime: 5_000,
  });

export const useCreateCotacao = (itemId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CotacaoCreate) =>
      apiFetch<Cotacao>(`/v1/itens/${itemId}/cotacoes`, { method: 'POST', body: input }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: cotacoesKey(itemId) });
    },
  });
};

export interface ExcluirCotacaoArgs {
  readonly cotacaoId: string;
  readonly motivo: string;
}

export const useExcluirCotacao = (itemId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cotacaoId, motivo }: ExcluirCotacaoArgs) =>
      apiFetch<Cotacao>(`/v1/itens/${itemId}/cotacoes/${cotacaoId}`, {
        method: 'DELETE',
        body: { motivo, tipo: 'manual' },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: cotacoesKey(itemId) });
      await qc.invalidateQueries({ queryKey: cestaKey(itemId) });
    },
  });
};

export interface SyncPncpResultDto {
  readonly inseridas: number;
  readonly duplicadas: number;
  readonly ignoradas_sem_preco: number;
  readonly janela_inicio: string;
  readonly janela_fim: string;
  readonly catmat: string | null;
  readonly catser: string | null;
}

export const useSyncPncp = (itemId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SyncPncpResultDto>(`/v1/itens/${itemId}/cotacoes/sync-pncp`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: cotacoesKey(itemId) });
    },
  });
};

// ============ cesta ============

export const useGetCesta = (itemId: string | undefined) =>
  useQuery({
    queryKey: cestaKey(itemId ?? '__none__'),
    queryFn: async () => {
      try {
        return await apiFetch<CestaDetail>(`/v1/itens/${itemId}/cesta`);
      } catch (e) {
        // 404 (cesta nao formada) e estado valido — UI mostra form em vez de erro
        const err = e as { status?: number };
        if (err.status === 404) return null;
        throw e;
      }
    },
    enabled: !!itemId,
    staleTime: 5_000,
  });

export const useFormarCesta = (itemId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CestaFormar) =>
      apiFetch<CestaDetail>(`/v1/itens/${itemId}/cesta`, { method: 'POST', body: input }),
    onSuccess: async (cesta) => {
      qc.setQueryData(cestaKey(itemId), cesta);
      // formacao pode marcar cotacoes como EXCLUIDA_OUTLIER no banco
      await qc.invalidateQueries({ queryKey: cotacoesKey(itemId) });
    },
  });
};
